import React from "react";
import Modal from "react-bootstrap/Modal";
import "./popup.css";

import { FaTimes } from "react-icons/fa";

// One card = one choice. The whole card is the click target (no CTA button);
// the outcome line at the bottom carries the affordance.
function Choice({ art, title, desc, outcome, badge, disabled, onPick }) {
  return (
    <div className="col-12 col-sm-6">
      <div
        className={`ac-card${disabled ? " ac-card--off" : ""}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : onPick}
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onPick();
              }
            }
        }
      >
        {badge && !disabled && <span className="ac-badge">{badge}</span>}
        <div className="ac-art-wrap">
          <img className="ac-art" src={art} alt="" />
        </div>
        <p className="ac-title">{title}</p>
        <p className="ac-desc">{desc}</p>
        <div className="ac-cta">
          <span>{outcome}</span>
          {!disabled && <span className="ac-arrow">→</span>}
        </div>
      </div>
    </div>
  );
}

function AutoCreateV2PopUp(props) {
  // Pull our custom props OUT so they are not spread onto <Modal> (which forwards
  // unknown props to its root DOM node → "React does not recognize the X prop"
  // warnings). Only `modalProps` (real react-bootstrap Modal props like show/onHide)
  // gets spread below.
  const {
    handleClick,
    onUpload,
    selectedPhotosCount,
    emptyBoxesCount: emptyBoxesCountProp,
    totalFillableBoxesCount: totalFillableBoxesCountProp,
    availableImagesCount: availableImagesCountProp,
    unusedImagesCount: unusedImagesCountProp,
    ...modalProps
  } = props;

  const emptyBoxes = emptyBoxesCountProp ?? 0;
  const totalFrames = totalFillableBoxesCountProp ?? 0;
  const available = availableImagesCountProp ?? 0;
  const unused = unusedImagesCountProp ?? available;
  const selected = selectedPhotosCount ?? 0;

  // Cards fire their option directly — no radio, no Apply step.
  const pick = (option) => {
    handleClick(option);
    modalProps.onHide();
  };

  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

  // Nothing to fill when every photo slot already has a photo in it.
  const fillDisabled = emptyBoxes === 0;
  // …and it can come up short, since it only draws from photos not used yet.
  const shortOfPhotos = !fillDisabled && unused < emptyBoxes;
  const willStayEmpty = emptyBoxes - unused;

  return (
    <Modal
      {...modalProps}
      size="lg"
      aria-labelledby="auto-design-title"
      centered
      className="auto-create-modal"
    >
      <div className="ac-head">
        <button className="ac-close" onClick={modalProps.onHide} aria-label="Close">
          <FaTimes size={14} />
        </button>
        <p className="ac-head-title" id="auto-design-title">
          How would you like your photos added?
        </p>
        <div className="ac-chips">
          <span className="ac-chip">{plural(available, "photo")} ready</span>
          {totalFrames > 0 && (
            <span className="ac-chip">
              {emptyBoxes > 0
                ? `${plural(emptyBoxes, "empty photo slot")}`
                : `All ${plural(totalFrames, "photo slot")} filled`}
            </span>
          )}
          {selected > 0 && (
            <span className="ac-chip ac-chip--accent">
              {selected} picked · we'll use {selected === 1 ? "it" : "these"} first
            </span>
          )}
        </div>
      </div>

      <Modal.Body className="px-3 pt-4 pb-3">
        <div className="row g-3 mx-0">
          <Choice
            art="images/photos/auto-create-fill.png"
            title="Fill the empty photo slots"
            desc="Leaves everything you've already designed untouched — we only drop photos into the slots that are still empty."
            outcome={
              fillDisabled
                ? "Every photo slot is already filled"
                : `Fills ${plural(emptyBoxes, "empty photo slot")}`
            }
            badge={emptyBoxes > 0 ? "Recommended" : null}
            disabled={fillDisabled}
            onPick={() => pick("option1")}
          />
          <Choice
            art="images/photos/auto-create-refresh.png"
            title="Start fresh with all my photos"
            desc="Clears the photos you've placed so far and fills every photo slot again from your whole library."
            outcome={
              totalFrames > 0
                ? `Refills all ${plural(totalFrames, "photo slot")}`
                : "Fills every page again"
            }
            onPick={() => pick("option2")}
          />
        </div>

        {shortOfPhotos && (
          <div className="ac-note mt-3">
            <span>
              You have {plural(unused, "photo")} left to place, but{" "}
              {plural(emptyBoxes, "empty photo slot")} —{" "}
              {plural(willStayEmpty, "slot")} will stay empty.
            </span>
            <button type="button" onClick={onUpload}>
              Add more photos
            </button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default AutoCreateV2PopUp;
